import React, { useState, useEffect } from 'react';
import Button from '../Button';

interface LineupFormData {
  total_lines: number;
  max_sets_per_line: number;
  allow_multi_line_selection: boolean;
  positions: Array<{ position_number: number; match_type: string }>;
}

interface LineupFormatStepProps {
  initialData: LineupFormData;
  onBack: () => void;
  onCreateTeam: (data: LineupFormData) => void;
  loading: boolean;
}

const MATCH_TYPES = [
  { value: 'mens_singles', label: "Men's Singles" },
  { value: 'womens_singles', label: "Women's Singles" },
  { value: 'mens_doubles', label: "Men's Doubles" },
  { value: 'womens_doubles', label: "Women's Doubles" },
  { value: 'mixed_doubles', label: 'Mixed Doubles' },
];

const LineupFormatStep: React.FC<LineupFormatStepProps> = ({
  initialData,
  onBack,
  onCreateTeam,
  loading,
}) => {
  const [formData, setFormData] = useState<LineupFormData>(initialData);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize positions when total_lines changes
  useEffect(() => {
    if (formData.positions.length !== formData.total_lines) {
      setFormData((prev) => {
        const newPositions = Array.from({ length: prev.total_lines }, (_, i) => ({
          position_number: i + 1,
          match_type: prev.positions[i]?.match_type || '',
        }));
        return { ...prev, positions: newPositions };
      });
    }
  }, [formData.total_lines, formData.positions.length]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (formData.total_lines < 1 || formData.total_lines > 15) {
      newErrors.total_lines = 'Number of lines must be between 1 and 15';
    }

    if (![1, 3, 5].includes(formData.max_sets_per_line)) {
      newErrors.max_sets_per_line = 'Max sets per line must be 1, 3, or 5';
    }

    formData.positions.forEach((pos) => {
      if (!pos.match_type) {
        newErrors[`position_${pos.position_number}`] = 'Match type is required for each line';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onCreateTeam(formData);
    }
  };

  const handlePositionMatchTypeChange = (positionNumber: number, matchType: string) => {
    setFormData({
      ...formData,
      positions: formData.positions.map((p) =>
        p.position_number === positionNumber ? { ...p, match_type: matchType } : p
      ),
    });
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">Step 2: Lineup Format Configuration</h2>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="total_lines" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Number of Lines <span className="text-red-500">*</span>
          </label>
          <select
            id="total_lines"
            value={formData.total_lines}
            onChange={(e) => setFormData({ ...formData, total_lines: parseInt(e.target.value) })}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
              errors.total_lines ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
            }`}
          >
            {Array.from({ length: 15 }, (_, i) => i + 1).map((num) => (
              <option key={num} value={num}>
                {num}
              </option>
            ))}
          </select>
          {errors.total_lines && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.total_lines}</p>}
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            On match day, how many matches are played? For instance, on a Saturday morning 5 unique
            lines of doubles are played (10 players in total)
          </p>
        </div>

        <div>
          <label htmlFor="max_sets_per_line" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Max Sets Per Line <span className="text-red-500">*</span>
          </label>
          <select
            id="max_sets_per_line"
            value={formData.max_sets_per_line}
            onChange={(e) => setFormData({ ...formData, max_sets_per_line: parseInt(e.target.value) })}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
              errors.max_sets_per_line ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
            }`}
          >
            <option value={1}>1</option>
            <option value={3}>3</option>
            <option value={5}>5</option>
          </select>
          {errors.max_sets_per_line && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.max_sets_per_line}</p>
          )}
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            For each line/match played, it is normally 3 sets - if 3rd set tie-break then still need 3
          </p>
        </div>

        <div>
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={formData.allow_multi_line_selection}
              onChange={(e) =>
                setFormData({ ...formData, allow_multi_line_selection: e.target.checked })
              }
              className="w-4 h-4 text-green-600 border-gray-300 dark:border-gray-600 rounded focus:ring-green-500 bg-white dark:bg-gray-700"
            />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Allow Multi-Line Selection <span className="text-red-500">*</span>
            </span>
          </label>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 ml-6">
            Can players be selected for multiple lines in a single match?
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Match Type for Each Line <span className="text-red-500">*</span>
          </label>
          <div className="space-y-3">
            {formData.positions.map((position) => (
              <div key={position.position_number}>
                <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                  Line {position.position_number}
                </label>
                <select
                  value={position.match_type}
                  onChange={(e) =>
                    handlePositionMatchTypeChange(position.position_number, e.target.value)
                  }
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
                    errors[`position_${position.position_number}`]
                      ? 'border-red-500'
                      : 'border-gray-300 dark:border-gray-600'
                  }`}
                >
                  <option value="">Select match type...</option>
                  {MATCH_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
                {errors[`position_${position.position_number}`] && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                    {errors[`position_${position.position_number}`]}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-between pt-4">
          <Button type="button" variant="outline" onClick={onBack} disabled={loading}>
            Back
          </Button>
          <Button type="submit" variant="primary" disabled={loading}>
            {loading ? 'Creating Team...' : 'Create Team'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default LineupFormatStep;

